import { useState, useEffect } from "react";
import axios from "../lib/api";
import { Code2, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";

const TrackingSetup = () => {
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchTrackingScript();
  }, []);

  const fetchTrackingScript = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/tracking/script`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setTrackingData(response.data);
    } catch (error) {
      console.error("Error fetching tracking script:", error);
      toast.error("Error al cargar el script de tracking");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(trackingData?.script || "");
    setCopied(true);
    toast.success("Script copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  const purchaseExample = `// Cuando se complete una compra, ejecuta:
ProfitSystem.track('purchase', {
  order_id: 'ORD-12345',
  revenue: 99.99,
  metadata: {
    product_name: 'Camiseta Premium',
    quantity: 1
  }
});`;

  const addToCartExample = `// Cuando el usuario añada al carrito:
ProfitSystem.track('add_to_cart', {
  metadata: {
    product_id: 'PROD-001',
    product_name: 'Camiseta Premium',
    price: 29.99
  }
});`;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="tracking-page">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900">Tracking de Primera Parte</h1>
          <p className="text-slate-600">Instala nuestro pixel para medir conversiones reales y detectar sobre-atribución</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        ) : (
          <>
            {/* API Key */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-900">Tu API Key</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <code className="flex-1 bg-slate-100 px-4 py-3 rounded-lg font-mono text-sm text-slate-700">
                    {trackingData?.api_key}
                  </code>
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(trackingData?.api_key || "");
                      toast.success("API Key copiada");
                    }}
                    data-testid="copy-api-key-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar
                  </Button>
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Guarda esta API key de forma segura. La necesitarás para el tracking server-side.
                </p>
              </CardContent>
            </Card>

            {/* Installation Script */}
            <Card className="bg-white border border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-bold text-slate-900">
                  Paso 1: Instala el Pixel Base
                </CardTitle>
                <Button
                  onClick={handleCopy}
                  className="bg-red-600 hover:bg-red-700 text-white btn-press"
                  data-testid="copy-script-btn"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copiar Script
                    </>
                  )}
                </Button>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">
                  Pega este código justo antes de cerrar la etiqueta <code className="bg-slate-100 px-1 rounded">&lt;/head&gt;</code> en todas las páginas de tu sitio:
                </p>
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-100 p-6 rounded-lg overflow-x-auto text-sm">
                    <code>{trackingData?.script}</code>
                  </pre>
                </div>
              </CardContent>
            </Card>

            {/* Track Purchase */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-900">
                  Paso 2: Trackea Compras
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">
                  En tu página de "Gracias por tu compra" o cuando se confirme un pedido, añade este código:
                </p>
                <pre className="bg-slate-900 text-slate-100 p-6 rounded-lg overflow-x-auto text-sm">
                  <code>{purchaseExample}</code>
                </pre>
              </CardContent>
            </Card>

            {/* Track Add to Cart */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-900">
                  Opcional: Trackea Add to Cart
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">
                  Para un tracking más completo del funnel, también puedes trackear cuando añaden al carrito:
                </p>
                <pre className="bg-slate-900 text-slate-100 p-6 rounded-lg overflow-x-auto text-sm">
                  <code>{addToCartExample}</code>
                </pre>
              </CardContent>
            </Card>

            {/* Event Types */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-900">Tipos de Eventos Soportados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { event: "page_view", desc: "Visita a página (automático)" },
                    { event: "purchase", desc: "Compra completada" },
                    { event: "add_to_cart", desc: "Añadir al carrito" },
                    { event: "begin_checkout", desc: "Iniciar checkout" },
                    { event: "view_item", desc: "Ver producto" },
                    { event: "sign_up", desc: "Registro de usuario" },
                    { event: "lead", desc: "Lead generado" },
                    { event: "custom", desc: "Evento personalizado" }
                  ].map((item) => (
                    <div key={item.event} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <code className="bg-red-100 text-red-700 px-2 py-1 rounded text-sm font-mono">
                        {item.event}
                      </code>
                      <span className="text-sm text-slate-600">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Why First Party */}
            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Code2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">¿Por qué tracking de primera parte?</h3>
                    <ul className="space-y-2 text-slate-300">
                      <li>• No depende de cookies de terceros (compatible con iOS 14+)</li>
                      <li>• Tú controlas los datos, no las plataformas de ads</li>
                      <li>• Captura UTMs para atribución precisa</li>
                      <li>• Compara datos reales vs lo que reportan Meta/Google</li>
                      <li>• Detecta sobre-atribución de hasta 30-50%</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TrackingSetup;
